/**
 * ScoutChatPanel — Floating-Action-Button (FAB) für Scout-Chat (TQ #6906 2026-06-03).
 *
 * Wird im Personal-Mode (with.it-sce.de) anstelle des AriaChatPanel
 * gerendert, solange der User KEIN Aria-Modul gebucht hat. Aria ist
 * Premium → Scout ist der niedrigschwellige öffentliche Berater-Chat,
 * der die Vor-Verkauf-Conversation übernimmt.
 *
 * API: GET /api/public/scout-history, POST /api/public/scout-chat (öffentlich,
 * kein Auth-Header). Session-ID wird in localStorage gehalten damit eingeloggte
 * User die Pre-Login-History sehen falls vorhanden.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../services/api'
import MarkdownMessage from './MarkdownMessage'
import { scopedLocalStorage, scopedSessionStorage } from '../lib/storage'

interface ScoutMsg {
  role: 'user' | 'assistant'
  content: string
  failed?: boolean
}

// TQ #8277/#8278: Props rein additiv/optional — ohne sie verhaelt sich das
// Panel exakt wie heute (passiver FAB, kein Auto-Open).
interface ScoutChatPanelProps {
  /** Wenn 'onboarding' gesetzt: proaktiver Auto-Open-Flow aktiviert. */
  autoOpenHint?: 'onboarding'
  /** Callback wenn der User das Onboarding per "Nicht mehr anzeigen" dismissed. */
  onAck?: () => void
}

// Guard: pro Browser-Session maximal EIN Auto-Open (sessionStorage).
const AUTO_OPEN_GUARD_KEY = 'scout_onboarding_autoopened'

function getSessionId(): string {
  try {
    let sid = scopedLocalStorage.getItem('scout_session_id')
    if (!sid) {
      sid = `scout-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
      scopedLocalStorage.setItem('scout_session_id', sid)
    }
    return sid
  } catch {
    return `scout-${Date.now()}`
  }
}

export default function ScoutChatPanel({ autoOpenHint, onAck }: ScoutChatPanelProps = {}) {
  const sessionId = useRef<string>(getSessionId())
  const [open, setOpen] = useState(false)
  // TQ #7544: Chatverlauf persistent — initial aus localStorage (instant +
  // navigations-robust). Backend-History (publicScoutHistory) ergaenzt nur, wenn
  // lokal leer (useEffect unten greift bei messages.length===0).
  const [messages, setMessages] = useState<ScoutMsg[]>(() => {
    try {
      const cached = scopedLocalStorage.getItem(`scout_messages_${sessionId.current}`)
      return cached ? JSON.parse(cached) : []
    } catch { return [] }
  })
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // TQ #8277/#8278: Onboarding-Auto-Open-State (nur relevant wenn autoOpenHint
  // gesetzt). `acked` lokal gespiegelt nach Dismiss; `badge` = roter Punkt am
  // FAB solange Auto-Open faellig/offen war und nicht acknowledged/geoeffnet.
  const onboardingMode = autoOpenHint === 'onboarding'
  const [acked, setAcked] = useState(false)
  const [badge, setBadge] = useState(false)
  // first_reply-Telemetrie genau einmal feuern.
  const firstReplyReported = useRef(false)

  useEffect(() => {
    if (!open) return
    if (messages.length > 0) return
    api.publicScoutHistory(sessionId.current)
      .then(history => { if (history.length > 0) setMessages(history) })
      .catch(() => { /* leere History ist OK */ })
  }, [open, messages.length])

  // TQ #8277/#8278: Proaktiver Onboarding-Auto-Open. Laeuft genau einmal beim
  // Mount, NUR im Onboarding-Kontext. Session-Guard verhindert Wiederholung.
  useEffect(() => {
    if (!onboardingMode) return
    let cancelled = false

    // Guard frueh pruefen: max 1x pro Browser-Session.
    let guardSet = false
    try {
      guardSet = scopedSessionStorage.getItem(AUTO_OPEN_GUARD_KEY) === '1'
    } catch { /* private mode: behandeln als nicht gesetzt */ }

    ;(async () => {
      let state
      try {
        state = await api.getOnboardingState()
      } catch {
        // Endpoint-Fehler: kein Crash, statische Starter-Karte traegt das Onboarding.
        return
      }
      if (cancelled) return

      if (state.acked) { setAcked(true); return }
      if (state.onboarding_stage !== 'new_no_booking') return
      // Bedingungen fuer Auto-Open
      if (guardSet || messages.length > 0) return

      // Guard SOFORT setzen, bevor der Opener-Call rausgeht (verhindert Doppel-Open
      // bei Re-Mount/StrictMode).
      try { scopedSessionStorage.setItem(AUTO_OPEN_GUARD_KEY, '1') } catch { /* egal */ }

      setOpen(true)
      setBadge(true)
      setLoading(true)
      setError(null)
      try {
        const response = await api.publicScoutChat('', sessionId.current, {
          opener: true,
          hint: 'onboarding',
          onboarding_stage: state.onboarding_stage,
        })
        if (!cancelled && response.response) {
          setMessages(prev => [...prev, { role: 'assistant', content: response.response }])
        }
      } catch {
        // Opener fehlgeschlagen: Panel bleibt nutzbar (User kann normal fragen).
        if (!cancelled) setError('Scout konnte die Begrüßung gerade nicht laden.')
      } finally {
        if (!cancelled) setLoading(false)
      }

      // Telemetrie: Auto-Open wurde gezeigt (best effort, Fehler ignorieren).
      api.scoutOnboardingAutoOpen().catch(() => { /* telemetrie nicht kritisch */ })
    })()

    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingMode])

  // Badge verschwindet sobald das Panel geoeffnet wird oder acked ist.
  useEffect(() => {
    if (open || acked) setBadge(false)
  }, [open, acked])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  // TQ #7544: Verlauf nach jeder Aenderung in localStorage spiegeln (ueberlebt
  // Navigation/Reload). Letzte 100 Nachrichten reichen.
  useEffect(() => {
    try {
      scopedLocalStorage.setItem(`scout_messages_${sessionId.current}`, JSON.stringify(messages.slice(-100)))
    } catch { /* quota/private-mode: nicht kritisch */ }
  }, [messages])

  const sendMessage = useCallback(async (text: string) => {
    const trimmed = text.trim()
    if (!trimmed || loading) return
    // TQ #8278: erster echter User-Reply im Onboarding-Kontext -> Telemetrie.
    if (onboardingMode && !firstReplyReported.current) {
      firstReplyReported.current = true
      api.scoutOnboardingAutoOpen(true).catch(() => { /* nicht kritisch */ })
    }
    setMessages(prev => [...prev, { role: 'user', content: trimmed }])
    setInput('')
    // TQ #7544: Cursor bleibt nach Senden im Feld (kein Re-Klick noetig).
    inputRef.current?.focus()
    setLoading(true)
    setError(null)
    try {
      const response = await api.publicScoutChat(trimmed, sessionId.current)
      setMessages(prev => [...prev, { role: 'assistant', content: response.response }])
    } catch (err: any) {
      setError(err?.error || err?.message || 'Scout konnte gerade nicht antworten.')
      setMessages(prev => {
        const copy = [...prev]
        for (let i = copy.length - 1; i >= 0; i--) {
          if (copy[i].role === 'user' && copy[i].content === trimmed) {
            copy[i] = { ...copy[i], failed: true }
            break
          }
        }
        return copy
      })
    } finally {
      setLoading(false)
    }
  }, [loading, onboardingMode])

  // TQ #8278: Dismiss ("Nicht mehr anzeigen"). Persistiert serverseitig (ack),
  // informiert das Layout (onAck) und unterdrueckt Badge/Auto-Open lokal.
  const handleDismiss = useCallback(() => {
    setAcked(true)
    setBadge(false)
    api.ackScoutOnboarding().catch(() => { /* best effort; lokal bereits acked */ })
    onAck?.()
  }, [onAck])

  // Badge wieder anzeigen, wenn das Panel im Onboarding-Kontext geschlossen
  // wurde ohne Ack (User soll den Hinweis nicht final verlieren).
  const handleClose = useCallback(() => {
    setOpen(false)
    if (onboardingMode && !acked) setBadge(true)
  }, [onboardingMode, acked])

  return (
    <>
      {/* FAB-Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label={badge ? 'Scout-Chat öffnen (neue Nachricht)' : 'Scout-Chat öffnen'}
          style={{
            position: 'fixed', right: 24, bottom: 24, zIndex: 1000,
            background: 'linear-gradient(135deg, #ff6b35, #f97316)',
            color: 'white', border: 'none', borderRadius: 999,
            padding: '0.9rem 1.4rem', fontSize: '0.95rem', fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem',
            boxShadow: '0 10px 25px rgba(249, 115, 22, 0.35)',
          }}
        >
          <span aria-hidden style={{ fontSize: '1.1rem' }}>💬</span>
          <span>Scout fragen</span>
          {/* TQ #8277: roter Indikator wenn ein proaktiver Opener aussteht. */}
          {badge && (
            <span
              aria-hidden
              style={{
                position: 'absolute', top: -4, right: -4,
                width: 14, height: 14, borderRadius: 999,
                background: '#ef4444', border: '2px solid white',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.05)',
              }}
            />
          )}
        </button>
      )}

      {/* Chat-Panel */}
      {open && (
        <div style={{
          position: 'fixed', right: 24, bottom: 24, zIndex: 1000,
          width: 380, maxWidth: 'calc(100vw - 48px)', maxHeight: 'calc(100vh - 48px)',
          display: 'flex', flexDirection: 'column',
          background: 'white', borderRadius: 16,
          boxShadow: '0 20px 50px rgba(0,0,0,0.18)',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #ff6b35, #f97316)',
            color: 'white', padding: '0.9rem 1rem',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span aria-hidden style={{ fontSize: '1.2rem' }}>💬</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Scout</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.85 }}>Berater-Chat · kostenlos</div>
              </div>
            </div>
            <button
              onClick={handleClose}
              aria-label="Chat schließen"
              style={{
                background: 'rgba(255,255,255,0.2)', color: 'white',
                border: 'none', borderRadius: 6, padding: '0.25rem 0.55rem',
                cursor: 'pointer', fontSize: '1rem',
              }}
            >×</button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '1rem',
            background: '#f9fafb', minHeight: 220,
          }}>
            {messages.length === 0 && !loading && (
              <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                Hi! Ich bin Scout, der IT-SCE Berater. Frag mich nach Services,
                Preisen oder ob etwas zu dir passt.
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{
                marginBottom: '0.75rem',
                display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
              }}>
                <div style={{
                  maxWidth: '85%', padding: '0.6rem 0.85rem', borderRadius: 12,
                  background: m.role === 'user'
                    ? (m.failed ? '#fee2e2' : '#f97316')
                    : 'white',
                  color: m.role === 'user' && !m.failed ? 'white' : '#111827',
                  border: m.role === 'assistant' ? '1px solid #e5e7eb' : 'none',
                  fontSize: '0.9rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {m.role === 'assistant'
                    ? <MarkdownMessage content={m.content} />
                    : m.content}
                  {m.failed && (
                    <div style={{ fontSize: '0.7rem', color: '#991b1b', marginTop: 4 }}>
                      Nicht zugestellt
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Scout tippt …</div>
            )}
            {error && (
              <div style={{
                marginTop: '0.5rem', padding: '0.5rem 0.75rem',
                background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: 6, fontSize: '0.85rem', color: '#991b1b',
              }}>{error}</div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* TQ #8278: "Nicht mehr anzeigen" — nur im Onboarding-Kontext und
              solange nicht acked. Persistiert per ack-Endpoint. */}
          {onboardingMode && !acked && (
            <div style={{
              padding: '0.6rem 0.75rem', borderTop: '1px solid #f3f4f6',
              background: '#fff7ed', fontSize: '0.8rem', color: '#7c2d12',
              display: 'flex', flexDirection: 'column', gap: '0.25rem',
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={false}
                  onChange={(e) => { if (e.target.checked) handleDismiss() }}
                  style={{ cursor: 'pointer' }}
                />
                <span>Nicht mehr anzeigen</span>
              </label>
              <span style={{ color: '#9a3412', opacity: 0.85, paddingLeft: '1.4rem' }}>
                Scout bleibt unten rechts verfügbar.
              </span>
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); sendMessage(input) }}
            style={{
              padding: '0.75rem', borderTop: '1px solid #e5e7eb',
              display: 'flex', gap: '0.5rem', background: 'white',
            }}
          >
            {/* TQ #7544: Input NICHT disabled waehrend loading — Nutzer kann parallel
                die naechste Frage tippen (Senden bleibt via Guard serialisiert). */}
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Frag etwas …"
              autoFocus
              style={{
                flex: 1, padding: '0.5rem 0.75rem', borderRadius: 8,
                border: '1px solid #d1d5db', fontSize: '0.9rem',
                outline: 'none',
              }}
            />
            <button
              type="submit"
              disabled={!input.trim() || loading}
              style={{
                padding: '0.5rem 1rem', background: '#f97316', color: 'white',
                border: 'none', borderRadius: 8, fontSize: '0.9rem', fontWeight: 500,
                cursor: !input.trim() || loading ? 'not-allowed' : 'pointer',
                opacity: !input.trim() || loading ? 0.6 : 1,
              }}
            >
              {loading ? '…' : 'Senden'}
            </button>
          </form>
        </div>
      )}
    </>
  )
}
